import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listProjects } from './api';
const PAGE_LIMIT = 20;
const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
];
function statusBadgeStyle(status) {
    switch (status) {
        case 'draft':
            return styles.badgeGray;
        case 'assigned':
            return styles.badgeBlue;
        case 'in_progress':
            return styles.badgeOrange;
        case 'completed':
            return styles.badgeGreen;
        default:
            return styles.badgeGray;
    }
}
function statusLabel(status) {
    switch (status) {
        case 'draft':
            return 'Draft';
        case 'assigned':
            return 'Assigned';
        case 'in_progress':
            return 'In Progress';
        case 'completed':
            return 'Completed';
    }
}
export const ProjectList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [nameSearch, setNameSearch] = useState('');
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listProjects({
                status: statusFilter || undefined,
                priority: priorityFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load projects');
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, priorityFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const resetPagination = () => {
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleStatusTab = (val) => {
        setStatusFilter(val);
        resetPagination();
    };
    const handlePriorityToggle = () => {
        setPriorityFilter((prev) => (prev === 'urgent' ? '' : 'urgent'));
        resetPagination();
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = tokenStack.slice(0, next);
        newStack.push(result.nextToken);
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    const filteredItems = result?.items.filter((p) => {
        if (!nameSearch.trim())
            return true;
        return p.customerName.toLowerCase().includes(nameSearch.toLowerCase()) ||
            p.title.toLowerCase().includes(nameSearch.toLowerCase());
    }) ?? [];
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Projects" }), _jsx("p", { style: styles.subheading, children: "Manage field service projects" })] }), _jsx("button", { style: styles.primaryButton, onClick: () => navigate('/projects/new'), children: "+ New Project" })] }), _jsx("div", { style: {
                        ...styles.tabsRow,
                        overflowX: isMobile ? 'auto' : 'visible',
                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                        flexWrap: isMobile ? 'nowrap' : 'wrap',
                        paddingBottom: isMobile ? '4px' : '0',
                    }, children: STATUS_TABS.map((tab) => (_jsx("button", { style: {
                            ...styles.tabButton,
                            ...(statusFilter === tab.value ? styles.tabButtonActive : {}),
                        }, onClick: () => handleStatusTab(tab.value), children: tab.label }, tab.value))) }), _jsxs("div", { style: {
                        ...styles.filtersCard,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                    }, children: [_jsx("input", { style: { ...styles.searchInput, minWidth: isMobile ? '0' : '200px' }, type: "text", placeholder: "Search by title or customer...", value: nameSearch, onChange: (e) => setNameSearch(e.target.value) }), _jsx("button", { style: {
                                ...styles.priorityToggle,
                                ...(priorityFilter === 'urgent' ? styles.priorityToggleActive : {}),
                                minHeight: isMobile ? '44px' : undefined,
                            }, onClick: handlePriorityToggle, children: priorityFilter === 'urgent' ? '🔴 Urgent Only' : 'All Priorities' }), _jsx("button", { style: {
                                ...styles.searchButton,
                                minHeight: isMobile ? '44px' : undefined,
                            }, onClick: load, children: "Refresh" })] }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading && !result && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), result && filteredItems.length === 0 && !loading && (_jsx("p", { style: styles.emptyText, children: "No projects found." })), filteredItems.length > 0 && (_jsx("div", { style: { opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }, children: isMobile ? (
                    /* Mobile card layout */
                    _jsx("div", { children: filteredItems.map((p) => (_jsx(ProjectMobileCard, { project: p, onClick: () => navigate(`/projects/${p.id}`) }, p.id))) })) : (
                    /* Desktop table layout */
                    _jsxs("div", { style: styles.listCard, children: [_jsxs("div", { style: styles.tableHeader, children: [_jsx("span", { style: { ...styles.col, flex: 3 }, children: "Title" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Customer" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Status" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Priority" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Installer" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Scheduled" })] }), filteredItems.map((p, idx) => (_jsx(ProjectRow, { project: p, isLast: idx === filteredItems.length - 1, onClick: () => navigate(`/projects/${p.id}`) }, p.id)))] })) })), result && (result.items.length > 0 || pageIndex > 0) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: styles.pageButton, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Previous" }), _jsxs("span", { style: styles.pageInfo, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: styles.pageButton, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const ProjectMobileCard = ({ project, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("div", { style: {
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : '#fff',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("div", { style: { marginBottom: '6px' }, children: _jsx("span", { style: { fontSize: '16px', fontWeight: '700', color: '#1d1d1f' }, children: project.title }) }), _jsx("p", { style: { margin: '0 0 6px 0', fontSize: '13px', color: '#86868b' }, children: project.customerName }), project.scheduledDate && (_jsx("p", { style: { margin: '0 0 6px 0', fontSize: '13px', color: '#86868b' }, children: new Date(project.scheduledDate).toLocaleDateString() })), _jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }, children: [_jsx("span", { style: statusBadgeStyle(project.status), children: statusLabel(project.status) }), project.priority === 'urgent' && (_jsx("span", { style: styles.badgeOrange, children: "Urgent" }))] })] }));
};
const ProjectRow = ({ project, isLast, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("div", { style: {
            ...styles.row,
            borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : 'transparent',
            cursor: 'pointer',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("span", { style: { ...styles.col, flex: 3, fontWeight: '600', color: '#1d1d1f' }, children: project.title }), _jsx("span", { style: { ...styles.col, flex: 2, color: '#3c3c43' }, children: project.customerName }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: _jsx("span", { style: statusBadgeStyle(project.status), children: statusLabel(project.status) }) }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: project.priority === 'urgent' ? (_jsx("span", { style: styles.badgeOrange, children: "Urgent" })) : (_jsx("span", { style: styles.badgeGray, children: "Normal" })) }), _jsx("span", { style: { ...styles.col, flex: 2, color: '#86868b' }, children: project.assignedInstallerName ?? 'Unassigned' }), _jsx("span", { style: { ...styles.col, flex: 1.5, color: '#86868b' }, children: project.scheduledDate
                    ? new Date(project.scheduledDate).toLocaleDateString()
                    : '—' })] }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '24px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    primaryButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
        whiteSpace: 'nowrap',
    },
    tabsRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    tabButton: {
        padding: '8px 18px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#86868b',
        cursor: 'pointer',
        flexShrink: 0,
    },
    tabButtonActive: {
        backgroundColor: '#0071e3',
        borderColor: '#0071e3',
        color: '#fff',
        fontWeight: '600',
    },
    filtersCard: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    searchInput: {
        flex: 1,
        minWidth: '200px',
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        outline: 'none',
    },
    priorityToggle: {
        padding: '10px 18px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#86868b',
        cursor: 'pointer',
    },
    priorityToggleActive: {
        backgroundColor: 'rgba(255,149,0,0.12)',
        borderColor: 'rgba(255,149,0,0.5)',
        color: '#c45c00',
        fontWeight: '600',
    },
    searchButton: {
        padding: '10px 20px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    listCard: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'opacity 0.2s ease',
    },
    tableHeader: {
        display: 'flex',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    col: {
        fontSize: '13px',
        color: '#86868b',
        fontWeight: '500',
        letterSpacing: '-0.1px',
        paddingRight: '12px',
    },
    row: {
        display: 'flex',
        padding: '16px 24px',
        alignItems: 'center',
        transition: 'background-color 0.15s ease',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeOrange: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(255,149,0,0.15)',
        color: '#c45c00',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyText: {
        textAlign: 'center',
        padding: '60px 24px',
        color: '#86868b',
        fontSize: '16px',
        margin: 0,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1d1d1f',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=ProjectList.js.map